import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Activity,
  ArrowRightLeft,
  UserCheck,
  Plus,
  Trash2,
  MessageSquare,
  AlertCircle,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { BuildActivityLogEntry } from '../../types/buildTeam';
import { buildTeamApiService } from '../../services/buildTeamApi';
import { usernameCacheService } from '../../services/usernameCache';
import { CreatorProfile } from '../CreatorProfile';
import { SectionHeader } from '../ui/SectionHeader';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonLoader } from '../SkeletonLoader';
import { cn } from '../../utils/cn';

const ACTION_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  status_change: ArrowRightLeft,
  assigned: UserCheck,
  created: Plus,
  deleted: Trash2,
  comment: MessageSquare,
};

function getActionIcon(action: string) {
  return ACTION_ICONS[action] ?? Activity;
}

export type BuildActivityTargetType = 'project' | 'build' | 'team_member';

export interface BuildActivityLogProps {
  targetId: string;
  targetType: BuildActivityTargetType;
}

function fetchActivity(
  targetType: BuildActivityTargetType,
  targetId: string
): Promise<BuildActivityLogEntry[]> {
  switch (targetType) {
    case 'project':
      return buildTeamApiService.getProjectActivity(targetId);
    case 'build':
      return buildTeamApiService.getBuildItemActivity(targetId);
    case 'team_member':
      return buildTeamApiService.getTeamMemberActivity(targetId);
  }
}

export function BuildActivityLog({ targetId, targetType }: BuildActivityLogProps) {
  const [entries, setEntries] = useState<BuildActivityLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchActivity(targetType, targetId)
      .then((data) => {
        if (!cancelled) {
          setEntries(data);
          const actorIds = data.map((e) => e.actor_id).filter(Boolean);
          if (actorIds.length > 0) {
            usernameCacheService.prefetchUsernames(actorIds);
          }
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load activity log');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [targetId, targetType]);

  if (loading) {
    return (
      <div className="space-y-4">
        <SkeletonLoader className="h-4 w-28" />
        <div className="space-y-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex gap-4">
              <SkeletonLoader className="w-6 h-6 rounded-full shrink-0" />
              <div className="flex-1 space-y-2">
                <SkeletonLoader className="h-4 w-2/3" />
                <SkeletonLoader className="h-3 w-40" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        bare
        compact
        tone="danger"
        icon={AlertCircle}
        title="Failed to load activity"
        description={error}
      />
    );
  }

  return (
    <div className="space-y-4">
      <SectionHeader title="Activity Log" icon={Activity} count={entries.length} className="mb-0" />

      {entries.length === 0 ? (
        <EmptyState
          bare
          compact
          icon={Activity}
          title="No activity yet"
          description="Changes to this record will appear here."
        />
      ) : (
        <div className="relative">
          <div className="absolute left-[11px] top-2 bottom-2 w-px bg-glass-200/15" />

          <AnimatePresence>
          <div className="space-y-0">
            {entries.map((entry) => {
              const Icon = getActionIcon(entry.action);
              const hasValueChange = entry.old_value != null && entry.new_value != null;
              return (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.12 }}
                  className={cn('relative flex gap-4', 'pb-5 last:pb-0')}
                >
                  <div className="relative z-10 flex-shrink-0 w-6 h-6 rounded-full bg-glass-100/10 border border-accent-500/40 flex items-center justify-center backdrop-blur-sm">
                    <Icon className="w-3 h-3 text-accent-300" />
                  </div>

                  <div className="flex-1 min-w-0 pt-0.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-glass-200">{entry.description}</span>
                      {hasValueChange && (
                        <span className="text-xs text-glass-500 font-mono">
                          {entry.old_value} → {entry.new_value}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <CreatorProfile creatorId={entry.actor_id} inline />
                      <span className="text-xs text-glass-500 tabular-nums">
                        {formatDistanceToNow(
                          new Date(
                            entry.created_at > 1e12 ? entry.created_at : entry.created_at * 1000
                          ),
                          { addSuffix: true }
                        )}
                      </span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
